// Části aplikace, které se stahují až při prvním otevření (React.lazy + Suspense): obrazovky sekcí a veřejné
// stránky mají vlastní chunky buildu, takže hlavní balík — a s ním první vykreslení na tabletu — neroste
// s každou další sekcí (docs/09 §12, rozdělení balíku 24. 9. 2026). Offline režim tabletu (4.5) se tím nemění:
// service worker má v precache všechny chunky (`globPatterns` ve vite.config.ts), takže se bez signálu načtou
// z tabletu stejně jako dřív hlavní balík.
//
// `lina()` vytvoří komponentu, `<NacitaniCasti>` ji obalí: dokud se kód stahuje, ukáže aplikační stav načítání
// (`Nacitani`, po krátké prodlevě — načtení z cache tabletu trvá desítky ms a nemá blikat). Když se stažení
// nepovede (tablet bez signálu dřív, než service worker uložil aplikaci do zařízení, nebo nová verze v jiné
// záložce mezitím smazala staré soubory), vysvětlí proč a nabídne Zkusit znovu a Obnovit aplikaci.
import {
  Component,
  createElement,
  lazy,
  Suspense,
  useEffect,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { TichyStav } from '../prihlaseni/TichyStav';
import { Nacitani } from './Nacitani';
import { PrazdnyStav } from './PrazdnyStav';
import './komponenty.css';

/** Za jak dlouho se ukáže indikátor načítání části aplikace (ms). */
export const PRODLEVA_NACITANI_MS = 200;

/** Kód části aplikace se nepodařilo stáhnout (odlišuje se tím od chyb při vykreslení). */
export class ChybaNacteniCasti extends Error {
  constructor(pricina: unknown) {
    super(pricina instanceof Error ? pricina.message : String(pricina), { cause: pricina });
    this.name = 'ChybaNacteniCasti';
  }
}

/** Části, jejichž stažení selhalo: obnovení (nový `React.lazy`) — zavolá je `NacitaniCasti`. */
const selhane = new Set<() => void>();

/** Příští vykreslení selhaných částí zkusí kód stáhnout znovu. */
function obnovitSelhane(): void {
  for (const obnovit of selhane) obnovit();
  selhane.clear();
}

/**
 * Líně načítaná komponenta: `lina(() => import('./zakazky/Zakazky').then((m) => m.Zakazky))`.
 * Na rozdíl od holého `React.lazy` jde po chybě stahování zkusit znovu: `React.lazy` si odmítnutí pamatuje
 * navždy, proto ho `NacitaniCasti` (Zkusit znovu, nové zobrazení obrazovky) vymění za nový. Výměna nesmí
 * přijít sama při vykreslení — React po odmítnutí vykresluje znovu a bez signálu by se stahovalo dokola.
 */
export function lina<P extends object>(nacti: () => Promise<ComponentType<P>>): ComponentType<P> {
  let nacitani: Promise<{ default: ComponentType<P> }> | null = null;
  const nactiJednou = () => {
    nacitani ??= nacti().then(
      (komponenta) => ({ default: komponenta }),
      (chyba: unknown) => {
        nacitani = null;
        selhane.add(obnovit);
        throw new ChybaNacteniCasti(chyba);
      },
    );
    return nacitani;
  };
  // `LazyExoticComponent` s obecnými props TypeScript do `createElement` nepustí (props → PropsWithRef<P>);
  // za běhu je to obyčejná komponenta, proto přetypování přes unknown.
  const vytvorit = () => lazy(nactiJednou) as unknown as ComponentType<P>;
  let Lina = vytvorit();
  function obnovit() {
    Lina = vytvorit();
  }
  return function LinaCast(props: P) {
    return createElement(Lina, props);
  };
}

/** Indikátor načítání až po prodlevě — rychlé načtení z cache se tak jen „objeví“. */
function NacitaniPoProdleve({ text }: { text?: string }) {
  const [viditelne, setViditelne] = useState(false);
  useEffect(() => {
    const casovac = window.setTimeout(() => setViditelne(true), PRODLEVA_NACITANI_MS);
    return () => window.clearTimeout(casovac);
  }, []);
  return viditelne ? (
    <div className="nacitani-casti">
      <Nacitani text={text} />
    </div>
  ) : null;
}

function ChybaNacteni({
  zkusitZnovu,
  celaStranka,
  naZavrit,
}: {
  zkusitZnovu: () => void;
  celaStranka: boolean;
  naZavrit?: () => void;
}) {
  const bezSignalu = typeof navigator !== 'undefined' && navigator.onLine === false;
  return (
    <div className={`nacitani-casti${celaStranka ? ' nacitani-casti--stranka' : ''}`} role="alert">
      <PrazdnyStav
        text={
          bezSignalu
            ? 'Tuhle část aplikace se nepodařilo načíst — jste bez signálu a v zařízení zatím uložená ' +
              'není. Zkuste to znovu, až bude signál.'
            : 'Tuhle část aplikace se nepodařilo načíst. Nejspíš mezitím vyšla nová verze aplikace — ' +
              'obnovte ji, nebo to zkuste znovu.'
        }
        akce={
          <div className="nacitani-casti__akce">
            <button type="button" className="tlacitko tlacitko--primarni" onClick={zkusitZnovu}>
              Zkusit znovu
            </button>
            {!bezSignalu && (
              <button
                type="button"
                className="tlacitko tlacitko--sekundarni"
                onClick={() => window.location.reload()}
              >
                Obnovit aplikaci
              </button>
            )}
            {naZavrit && (
              <button type="button" className="tlacitko tlacitko--sekundarni" onClick={naZavrit}>
                Zavřít
              </button>
            )}
          </div>
        }
      />
    </div>
  );
}

interface PropsNacitani {
  children: ReactNode;
  /** Text indikátoru načítání (výchozí „Načítám…“). */
  text?: string;
  /**
   * Stránka mimo shell (veřejné odkazy pro zákazníky): místo indikátoru v obsahu celostránkový tichý stav
   * jako při ověřování přihlášení.
   */
  celaStranka?: boolean;
  /**
   * Doplňkový blok, který se často neukáže vůbec (připomínky na Domů): bez indikátoru načítání, a když se
   * kód nestáhne, blok prostě chybí — žádná chybová hláška.
   */
  tiche?: boolean;
  /** Zásuvka nebo okno: při chybě stažení i „Zavřít“, ať chyba nezůstane viset na obrazovce (kontrola #761, D1). */
  naZavrit?: () => void;
}

interface StavNacitani {
  /** Zachycená chyba (obalená, protože vyhodit jde cokoli, i `null`). */
  zachyceno: { chyba: unknown } | null;
}

/**
 * Obal líně načítaných částí: Suspense s aplikačním stavem načítání a zachycení chyby stažení kódu.
 * Jiné chyby pošle dál výš — chování aplikace se kvůli rozdělení balíku nemění.
 */
export class NacitaniCasti extends Component<PropsNacitani, StavNacitani> {
  override state: StavNacitani = { zachyceno: null };

  constructor(props: PropsNacitani) {
    super(props);
    // Nově zobrazená obrazovka (návrat do sekce, znovu Domů) zkusí dřív selhané stažení znovu — ještě
    // před prvním vykreslením, jinak by hned ukázala starou chybu.
    obnovitSelhane();
  }

  static getDerivedStateFromError(chyba: unknown): StavNacitani {
    return { zachyceno: { chyba } };
  }

  private zkusitZnovu = () => {
    obnovitSelhane();
    this.setState({ zachyceno: null });
  };

  override render() {
    const { zachyceno } = this.state;
    if (zachyceno) {
      // Chybu vykreslení obrazovky tenhle obal neřeší — vyhozená z render() jde k dalšímu obalu výš.
      if (!(zachyceno.chyba instanceof ChybaNacteniCasti)) throw zachyceno.chyba;
      // Doplňkový blok bez signálu prostě chybí; zkusí se znovu při dalším zobrazení obrazovky.
      if (this.props.tiche) return null;
      return (
        <ChybaNacteni
          zkusitZnovu={this.zkusitZnovu}
          celaStranka={this.props.celaStranka ?? false}
          naZavrit={this.props.naZavrit}
        />
      );
    }
    const { text, celaStranka, tiche } = this.props;
    let nahradni: ReactNode = <NacitaniPoProdleve text={text} />;
    if (celaStranka) nahradni = <TichyStav text={text} />;
    else if (tiche) nahradni = null;
    return <Suspense fallback={nahradni}>{this.props.children}</Suspense>;
  }
}
